import {
  ALL_CONTENT_REVIEW_STATUSES,
  ALL_REVIEW_STATUSES,
  CONTENT_REVIEW_STATUS_META,
  isContentReviewStatus,
  isReviewStatus,
  REVIEW_STATUS_META,
  type ContentReviewStatus,
  type ReviewStatus,
} from "@zenforge/shared";
import { html } from "@codemirror/lang-html";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import CodeMirror, { type ReactCodeMirrorRef } from "@uiw/react-codemirror";
import { useEffect, useRef, useState } from "react";
import { api, uploadFile } from "./api.js";
import { usePermission } from "./auth-context.js";
import { ActionButton } from "./components.js";
import { ContentPreview, type ResponseType } from "./ContentPreview.js";

/** CodeMirror's language extension for the response type being edited —
 *  matches ContentPreview's own detection so the editor highlights the same
 *  syntax the rendered preview is interpreting it as. */
function languageExtension(responseType: ResponseType | undefined) {
  if (responseType === "json") return json();
  if (responseType === "markdown") return markdown();
  return html();
}

const ALLOWED_IMAGE_TYPES = "image/png,image/jpeg,image/webp,image/gif";
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

/** Swaps every presigned image URL back to its portable `relative:` src
 *  before saving — mirrors the main app admin portal's own
 *  replacePresignedUrls (Quill.jsx), since content edited here is pushed
 *  into the exact same chapter_module_content HTML that function guards. A
 *  presigned S3 URL expires; the relative path is what must actually be
 *  stored. */
function replacePresignedUrls(html: string, urlMap: Map<string, string>): string {
  if (!html || urlMap.size === 0) return html;
  let result = html;
  for (const [presignedUrl, relativeSrc] of urlMap) {
    result = result.replaceAll(presignedUrl, relativeSrc);
  }
  return result;
}

/**
 * The reviewer's verdict on one row — a pipeline execution, a generation
 * job, or a generated_contents row — and the note behind it.
 *
 * Shared across GenerationQueueList, PipelineExecutionsList and
 * ContentReviewSection: the different kinds of row have separate schemas and
 * separate review endpoints (see migration 057's reasoning for why the
 * tables were duplicated rather than shared), but the control itself has
 * nothing kind-specific in it beyond which URL to POST to — forking the
 * component would just be copies of the same JSX to keep in sync by hand.
 *
 * Advisory: setting a verdict publishes nothing and blocks nothing. It
 * records what a reviewer found so whoever holds content:approve can
 * decide. That is why this is gated on content:review and sits apart from
 * the approve action.
 */
export function ReviewControl({
  kind,
  exec,
  onChanged,
  toast,
}: {
  kind: "pipeline-execution" | "generation-job" | "content";
  exec: any;
  onChanged: () => void;
  toast: (m: string) => void;
}) {
  const canReview = usePermission("content:review");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState<string | null>(null);

  const reviewPath =
    kind === "pipeline-execution"
      ? `/pipelines/executions/${exec.id}/review`
      : kind === "generation-job"
        ? `/generation-jobs/${exec.id}/review`
        : `/generated-contents/${exec.id}/review`;

  // The content row's own copy of these fields is named differently
  // (contentReviewedByUserName/contentReviewedAt) because generated_contents
  // already had reviewedByUserId/reviewedByUserName for a different thing —
  // who last edited reviewedContent — before migration 059 added this axis.
  const reviewedByName =
    kind === "content" ? exec.contentReviewedByUserName : exec.reviewedByUserName;
  const reviewedAt = kind === "content" ? exec.contentReviewedAt : exec.reviewedAt;

  // Content review is a plain reviewed/not-reviewed flag (migration 060),
  // distinct from the richer 5-value verdict pipeline executions and
  // generation jobs use — see content-review-status.ts for why.
  const isContent = kind === "content";
  const contentCurrent: ContentReviewStatus = isContentReviewStatus(exec.reviewStatus ?? "")
    ? (exec.reviewStatus as ContentReviewStatus)
    : "NOT_REVIEWED";
  const jobCurrent: ReviewStatus = isReviewStatus(exec.reviewStatus ?? "")
    ? (exec.reviewStatus as ReviewStatus)
    : "PENDING";

  async function setStatus(next: string) {
    setSaving(next);
    try {
      await api.post(reviewPath, {
        reviewStatus: next,
        note: note.trim() || null,
      });
      // Refresh first: the verdict is saved by this point, so a failure in
      // anything cosmetic afterwards must not leave the row showing the old
      // status.
      setNote("");
      onChanged();
      const label = isContent
        ? CONTENT_REVIEW_STATUS_META[next as ContentReviewStatus].label
        : REVIEW_STATUS_META[next as ReviewStatus].label;
      toast(`Review set to ${label}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to set review status");
    } finally {
      setSaving(null);
    }
  }

  const currentBadge = isContent
    ? CONTENT_REVIEW_STATUS_META[contentCurrent].badge
    : REVIEW_STATUS_META[jobCurrent].badge;
  const currentLabel = isContent
    ? CONTENT_REVIEW_STATUS_META[contentCurrent].label
    : REVIEW_STATUS_META[jobCurrent].label;

  return (
    <div
      style={{
        marginBottom: 14,
        padding: 12,
        border: "1px solid var(--line)",
        borderRadius: 8,
        background: "var(--bg)",
      }}
    >
      <div className="row between" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div className="row" style={{ gap: 8, alignItems: "center" }}>
          <b style={{ fontSize: 13 }}>Reviewer status</b>
          <span className={`badge ${currentBadge} mini`}>{currentLabel}</span>
          {reviewedByName && (
            <span className="mini muted">
              by {reviewedByName}
              {reviewedAt ? ` · ${new Date(reviewedAt).toLocaleString()}` : ""}
            </span>
          )}
        </div>
        <span className="mini muted">Advisory — does not approve or publish</span>
      </div>

      {exec.reviewNote && (
        <div
          className="mini"
          style={{ marginTop: 8, fontStyle: "italic", whiteSpace: "pre-wrap" }}
        >
          “{exec.reviewNote}”
        </div>
      )}

      {canReview && (
        <div style={{ marginTop: 10 }}>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note — what you checked, or what needs fixing"
            rows={4}
            style={{ width: "100%", marginBottom: 8, resize: "vertical", fontFamily: "inherit" }}
          />
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            {isContent
              ? ALL_CONTENT_REVIEW_STATUSES.filter((st) => st !== contentCurrent).map((st) => (
                  <ActionButton
                    key={st}
                    className="mini"
                    pending={saving === st}
                    disabled={saving !== null}
                    title={CONTENT_REVIEW_STATUS_META[st].hint}
                    onClick={(e) => {
                      e.stopPropagation();
                      void setStatus(st);
                    }}
                  >
                    {CONTENT_REVIEW_STATUS_META[st].label}
                  </ActionButton>
                ))
              : ALL_REVIEW_STATUSES.filter((st) => st !== jobCurrent).map((st) => (
                  <ActionButton
                    key={st}
                    className="mini"
                    pending={saving === st}
                    disabled={saving !== null}
                    title={REVIEW_STATUS_META[st].hint}
                    onClick={(e) => {
                      e.stopPropagation();
                      void setStatus(st);
                    }}
                  >
                    {REVIEW_STATUS_META[st].label}
                  </ActionButton>
                ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The editable body of one generated_contents row, plus the reviewer status
 * + remarks control for it.
 *
 * Three top-level tabs — Content, Mobile Content (only when the row's
 * destination has one), Review — so only one concern is visible at a time.
 * This used to be one scrolling column stacking all three, which got
 * cramped once mobile content and the review control were added on top of
 * the original Original/Edited toggle.
 *
 * Read-only preview by default — a reviewer opts into "Edit" explicitly
 * rather than the box always being a textarea, so the common case (just
 * reading the output) is not made noisier for the sake of the occasional
 * edit. "Original" and "Edited" are two tabs rather than a side-by-side diff:
 * the content here is HTML/Markdown/JSON meant to be read rendered, and a
 * true diff view only helps for plain text.
 *
 * Shared between Queue.tsx's plain content modal and SourcedContentView's
 * split PDF/content pane — both preview one generated_contents row, so the
 * edit/review UI is identical regardless of which surface opened it.
 */
export function ContentReviewSection({
  contentId,
  generatedContent,
  reviewedContent,
  responseType,
  toast,
  onSaved,
  onApproved,
}: {
  contentId: number;
  generatedContent: string;
  reviewedContent: string | null;
  responseType?: ResponseType;
  toast: (m: string) => void;
  onSaved: (reviewedContent: string | null) => void;
  /** Called after a successful approve — lets the caller refresh whatever
   *  row list is showing this content (e.g. PipelineExecutionsList's
   *  localRefreshKey), the same way the row-level Approve & Push button
   *  already does. Optional: a caller with no list to refresh can omit it. */
  onApproved?: () => void;
}) {
  const canEdit = usePermission("content:reject");
  const canApprove = usePermission("content:approve");
  const [approving, setApproving] = useState(false);
  const hasEdit = reviewedContent !== null && reviewedContent !== undefined;
  const [section, setSection] = useState<"content" | "mobile" | "review">("content");
  const [tab, setTab] = useState<"edited" | "original">(hasEdit ? "edited" : "original");
  // One shared edit mode for both Content and Mobile Content: either tab's
  // Edit button turns it on, and Save commits both drafts together in one
  // request — see save() below.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(reviewedContent ?? generatedContent);
  // Mobile content is only ever known via `detail` (GET /generated-contents/:id
  // carries reviewedMobileContent + supportsMobileContent; the props this
  // component receives don't), so it can't be seeded until that fetch lands —
  // see the `detail` effect below, which seeds it the first time detail
  // arrives for a given contentId.
  const [mobileDraft, setMobileDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  console.log("🚀 -- ContentReviewSection -- detail:", detail, section);

  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const contentEditorRef = useRef<ReactCodeMirrorRef>(null);
  const mobileEditorRef = useRef<ReactCodeMirrorRef>(null);
  // presigned url -> relative: src, built up as images are inserted this edit
  // session — swapped back into both drafts right before save() posts them,
  // same bookkeeping the admin portal's imageUrlMapRef does.
  const imageUrlMapRef = useRef(new Map<string, string>());

  useEffect(() => {
    setSection("content");
    setTab(hasEdit ? "edited" : "original");
    setDraft(reviewedContent ?? generatedContent);
    setEditing(false);
    setMobileDraft(null);
  }, [contentId, reviewedContent, generatedContent]);

  useEffect(() => {
    let cancelled = false;
    api
      .get<any>(`/generated-contents/${contentId}`)
      .then((c) => {
        if (cancelled) return;
        setDetail(c);
        setMobileDraft((current) =>
          current === null ? (c.reviewedMobileContent ?? generatedContent) : current,
        );
        // c.imageUrlsByPath is { relativePath: presignedUrl } — the server
        // just resolved reviewedContent/reviewedMobileContent for display.
        // Seed the map in the direction save() needs (presignedUrl ->
        // relativePath) so an image resolved from a PAST session, not just
        // one uploaded this session, can still be swapped back correctly —
        // without this, saving unedited content that merely displayed an
        // old image would silently persist the expiring presigned URL.
        const urlsByPath = c.imageUrlsByPath as Record<string, string> | undefined;
        if (urlsByPath) {
          for (const [relativePath, presignedUrl] of Object.entries(urlsByPath)) {
            imageUrlMapRef.current.set(
              presignedUrl,
              relativePath.startsWith("relative:") ? relativePath : `relative:${relativePath}`,
            );
          }
        }
      })
      .catch(() => {
        // The review control below just won't show reviewer/name details —
        // the content itself is already loaded by the caller.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- generatedContent
    // is only a fallback for the initial seed, re-running this on every
    // change to it would re-fetch for no reason.
  }, [contentId, detailRefreshKey]);

  const supportsMobileContent = detail?.supportsMobileContent === true;
  const hasMobileEdit = detail?.reviewedMobileContent != null;

  async function save() {
    setSaving(true);
    try {
      const urlMap = imageUrlMapRef.current;
      const finalContent = replacePresignedUrls(draft, urlMap);
      const finalMobileContent =
        mobileDraft === null ? null : replacePresignedUrls(mobileDraft, urlMap);
      await api.put(`/generated-contents/${contentId}`, {
        reviewedContent: finalContent,
        ...(supportsMobileContent
          ? { reviewedMobileContent: finalMobileContent === generatedContent ? null : finalMobileContent }
          : {}),
      });
      // The parent renders whatever it is handed here, so it gets the draft
      // (presigned, loadable URLs) — not finalContent, whose `relative:` srcs
      // are for storage only and show as broken images. The map is kept so a
      // later edit of this same content can still reverse these URLs; stale
      // entries are harmless since they only match an exact URL.
      onSaved(draft);
      setEditing(false);
      setTab("edited");
      // Refreshes `detail` so the "Edited by / at" line — and the mobile
      // draft seed above — reflect this save immediately rather than
      // whoever/whenever the row was last opened.
      setDetailRefreshKey((k) => k + 1);
      toast("Edit saved");
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to save edit");
    } finally {
      setSaving(false);
    }
  }

  function startEditing() {
    setDraft(shown);
    setEditing(true);
  }

  /** Publishes this content to the main app — same endpoint and effect as
   *  the row-level "✅ Approve & Push" button on the executions/queue list,
   *  just reachable without leaving this modal. */
  async function approve() {
    setApproving(true);
    try {
      await api.post(`/generated-contents/${contentId}/approve`, {});
      toast("Content approved & pushed to main app!");
      setDetailRefreshKey((k) => k + 1);
      onApproved?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to approve content");
    } finally {
      setApproving(false);
    }
  }

  /** Opens a file picker, uploads to ZenForge's own /content-images route
   *  (which proxies to the main app's content-image endpoint using the
   *  target CodeMirror instance's own draft/setter), and inserts
   *  `<img src=presigned data-relative-src=relative>` at the cursor —
   *  mirrors Quill.jsx's handleHtmlImageUpload on the admin portal. */
  function insertImage(
    editorRef: React.RefObject<ReactCodeMirrorRef>,
    setDraftValue: (updater: (current: string) => string) => void,
  ) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ALLOWED_IMAGE_TYPES;
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > MAX_IMAGE_BYTES) {
        toast("Image must be under 5 MB.");
        return;
      }
      setUploadingImage(true);
      try {
        const { url, src } = await uploadFile<{ url: string; src: string }>(
          `/generated-contents/${contentId}/images`,
          file,
          {},
        );
        imageUrlMapRef.current.set(url, src);
        const imgTag = `<img src="${url}" data-relative-src="${src}" />`;

        const view = editorRef.current?.view;
        if (view) {
          const pos = view.state.selection.main.head;
          view.dispatch({ changes: { from: pos, to: pos, insert: imgTag } });
          view.focus();
        } else {
          // Editor not mounted yet (shouldn't happen while editing is true,
          // but falls back to appending rather than silently dropping the
          // upload).
          setDraftValue((current) => current + imgTag);
        }
      } catch (err) {
        toast(err instanceof Error ? err.message : "Image upload failed");
      } finally {
        setUploadingImage(false);
      }
    };
    input.click();
  }

  const shown = tab === "edited" ? (reviewedContent ?? generatedContent) : generatedContent;

  const saveBar = editing && (
    <div className="row" style={{ gap: 8, marginTop: 8 }}>
      <ActionButton className="mini primary" pending={saving} onClick={() => void save()}>
        Save Edit
      </ActionButton>
      <button
        type="button"
        className="btn mini"
        disabled={saving}
        onClick={() => {
          setDraft(reviewedContent ?? generatedContent);
          setMobileDraft(detail?.reviewedMobileContent ?? generatedContent);
          setEditing(false);
        }}
      >
        Cancel
      </button>
    </div>
  );

  return (
    <div>
      <div className="row" style={{ gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        <button
          type="button"
          className={`btn mini${section === "content" ? " active" : ""}`}
          onClick={() => setSection("content")}
        >
          🖥️ Content
        </button>
        {supportsMobileContent && (
          <button
            type="button"
            className={`btn mini${section === "mobile" ? " active" : ""}`}
            onClick={() => setSection("mobile")}
          >
            📱 Mobile Content{hasMobileEdit ? "" : " (mirrors content)"}
          </button>
        )}
        <button
          type="button"
          className={`btn mini${section === "review" ? " active" : ""}`}
          onClick={() => setSection("review")}
        >
          Review
          {detail && (() => {
            const status: ContentReviewStatus = isContentReviewStatus(detail.reviewStatus ?? "")
              ? (detail.reviewStatus as ContentReviewStatus)
              : "NOT_REVIEWED";
            const meta = CONTENT_REVIEW_STATUS_META[status];
            return (
              <span className={`badge mini ${meta.badge}`} style={{ marginLeft: 6 }}>
                {meta.label}
              </span>
            );
          })()}
        </button>
      </div>

      {section === "content" && (
        <div>
          <div className="row between" style={{ gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div className="row" style={{ gap: 6 }}>
              <button
                type="button"
                className={`btn mini${tab === "original" ? " active" : ""}`}
                onClick={() => {
                  setTab("original");
                  setEditing(false);
                }}
              >
                Original
              </button>
              <button
                type="button"
                className={`btn mini${tab === "edited" ? " active" : ""}`}
                onClick={() => setTab("edited")}
              >
                Edited{!hasEdit ? " (none yet)" : ""}
              </button>
            </div>

            {canEdit && !editing && (
              <button
                type="button"
                className="btn mini"
                title={
                  tab === "original"
                    ? "Start an edit from the model's original output"
                    : "Continue editing the current draft"
                }
                onClick={startEditing}
              >
                ✏️ Edit
              </button>
            )}
            {canEdit && editing && (
              <button
                type="button"
                className="btn mini"
                disabled={uploadingImage}
                title="Insert an image at the cursor"
                onClick={() => insertImage(contentEditorRef, setDraft)}
              >
                {uploadingImage ? "Uploading…" : "🖼️ Insert Image"}
              </button>
            )}
          </div>

          {hasEdit && (
            <div className="mini muted" style={{ marginBottom: 8 }}>
              ✏️ Edited
              {detail?.reviewedByUserName ? ` by ${detail.reviewedByUserName}` : ""}
              {detail?.updatedAt ? ` · ${new Date(detail.updatedAt).toLocaleString()}` : ""}
            </div>
          )}

          {editing ? (
            <div>
              <div
                style={{
                  display: "grid",
                  // Editor and rendered preview side by side so a change is
                  // checked against how it will actually look without saving
                  // first — below a certain width the two panes are too narrow
                  // to be useful, so it collapses to editor-over-preview.
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  gap: 10,
                }}
                className="content-edit-split"
              >
                <div style={{ border: "1px solid var(--line)", borderRadius: 6, overflow: "hidden", minWidth: 0 }}>
                  <CodeMirror
                    ref={contentEditorRef}
                    value={draft}
                    onChange={(value) => setDraft(value)}
                    extensions={[languageExtension(responseType)]}
                    height="420px"
                    basicSetup={{ foldGutter: true, highlightActiveLine: true }}
                  />
                </div>
                <div
                  style={{
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                    minWidth: 0,
                    height: 420,
                    overflow: "auto",
                    padding: 10,
                    background: "var(--panel)",
                  }}
                >
                  <ContentPreview content={draft} responseType={responseType ?? "html"} showSourceTab={false} />
                </div>
              </div>
              {saveBar}
              {hasEdit && (
                <div className="row" style={{ gap: 8, marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn mini"
                    disabled={saving}
                    title="Discard the edit and go back to what the model produced"
                    onClick={() => setDraft(generatedContent)}
                  >
                    ↺ Reset to Original
                  </button>
                </div>
              )}
            </div>
          ) : (
            <ContentPreview content={shown} responseType={responseType ?? "html"} showSourceTab={false} />
          )}
        </div>
      )}

      {section === "mobile" && supportsMobileContent && (
        <div>
          <div className="row between" style={{ gap: 8, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span className="mini muted">
              {hasMobileEdit
                ? "Edited separately from the content tab"
                : "Not edited — mirrors the content tab until you edit it"}
            </span>
            {canEdit && !editing && (
              <button type="button" className="btn mini" title="Start editing" onClick={startEditing}>
                ✏️ Edit
              </button>
            )}
            {canEdit && editing && (
              <button
                type="button"
                className="btn mini"
                disabled={uploadingImage}
                title="Insert an image at the cursor"
                onClick={() =>
                  insertImage(mobileEditorRef, (updater) => setMobileDraft((current) => updater(current ?? "")))
                }
              >
                {uploadingImage ? "Uploading…" : "🖼️ Insert Image"}
              </button>
            )}
          </div>

          {editing ? (
            <div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                  gap: 10,
                }}
                className="content-edit-split"
              >
                <div style={{ border: "1px solid var(--line)", borderRadius: 6, overflow: "hidden", minWidth: 0 }}>
                  <CodeMirror
                    ref={mobileEditorRef}
                    value={mobileDraft ?? ""}
                    onChange={(value) => setMobileDraft(value)}
                    extensions={[languageExtension(responseType)]}
                    height="420px"
                    basicSetup={{ foldGutter: true, highlightActiveLine: true }}
                  />
                </div>
                <div
                  style={{
                    border: "1px solid var(--line)",
                    borderRadius: 6,
                    minWidth: 0,
                    height: 420,
                    overflow: "auto",
                    padding: 10,
                    background: "var(--panel)",
                    // Capped and centered to roughly a phone's viewport width
                    // — a full-width preview reads as the web layout, which
                    // is exactly the distinction this tab exists to show.
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ width: "100%", maxWidth: 390 }}>
                    <ContentPreview content={mobileDraft ?? ""} responseType={responseType ?? "html"} showSourceTab={false} />
                  </div>
                </div>
              </div>
              {saveBar}
              <div className="row" style={{ gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="btn mini"
                  disabled={saving}
                  title="Discard the mobile-specific edit and mirror the content tab again"
                  onClick={() => setMobileDraft(generatedContent)}
                >
                  ↺ Reset to Original
                </button>
                <button
                  type="button"
                  className="btn mini"
                  disabled={saving}
                  title="Copy the content tab's current draft into the mobile draft"
                  onClick={() => setMobileDraft(draft)}
                >
                  ⧉ Copy from content
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={{ width: "100%", maxWidth: 390 }}>
                <ContentPreview content={mobileDraft ?? shown} responseType={responseType ?? "html"} showSourceTab={false} />
              </div>
            </div>
          )}
        </div>
      )}

      {section === "review" && detail && (
        <div>
          {canApprove && (
            <div style={{ marginBottom: 14 }}>
              {detail.status === "GENERATED" ? (
                <ActionButton
                  className="mini success"
                  pending={approving}
                  onClick={() => void approve()}
                >
                  ✅ Approve & Push
                </ActionButton>
              ) : detail.status === "APPROVED" ? (
                <span className="mini muted">✅ Already approved & pushed</span>
              ) : (
                <span className="mini muted" title="Only generated (not yet reviewed) content can be approved">
                  Approve & Push unavailable — content status is {detail.status}
                </span>
              )}
            </div>
          )}
          <ReviewControl
            kind="content"
            exec={detail}
            toast={toast}
            onChanged={() => setDetailRefreshKey((k) => k + 1)}
          />
        </div>
      )}
    </div>
  );
}
